"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, Lock, Smartphone, Hash, Key } from "lucide-react";
import { useAuthStore } from "../store/authStore";

interface ResetForm {
  email: string;
  mobile: string;
  gstNo: string;
  tally_serialno: string;
}

interface ConfirmResetForm {
  newPassword: string;
  confirmPassword: string;
}

export default function ResetPasswordPage() {
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [message, setMessage] = useState("");
  const { resetPassword, confirmResetPassword, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const {
    register: registerRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: errorsRequest },
    reset: resetRequest,
  } = useForm<ResetForm>();
  const {
    register: registerConfirm,
    handleSubmit: handleSubmitConfirm,
    formState: { errors: errorsConfirm },
    watch,
  } = useForm<ConfirmResetForm>();

  const watchPassword = watch("newPassword");

  useEffect(() => {
    if (token) {
      setStep("confirm");
    } else {
      setStep("request");
    }
  }, [token]);

  const onSubmitRequest = async (data: ResetForm) => {
    const success = await resetPassword(data);
    if (success) {
      setMessage("✅ Reset link sent! Please check your email.");
      resetRequest(); // Optional: clear form fields

      // Wait 2 seconds before redirecting to login
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    }
  };

  const onSubmitConfirm = async (data: ConfirmResetForm) => {
    if (!token) return;
    const success = await confirmResetPassword(token, data.newPassword);
    if (success) {
      setMessage("✅ Password reset successfully! Redirecting to login...");

      // Wait so the success toast/message is visible before navigating away
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="glass-effect rounded-2xl p-8 shadow-2xl">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-center mb-8"
          >
            <div className="banking-gradient w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              {step === "request" ? (
                <Mail className="w-8 h-8 text-white" />
              ) : (
                <Lock className="w-8 h-8 text-white" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {step === "request" ? "Reset Password" : "Set New Password"}
            </h1>
            <p className="text-gray-300">
              {step === "request"
                ? "Enter your email to receive reset instructions"
                : "Enter your new password below"}
            </p>
          </motion.div>

          {step === "request" ? (
            <form
              onSubmit={handleSubmitRequest(onSubmitRequest)}
              className="space-y-6"
            >
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  {...registerRequest("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email address",
                    },
                  })}
                  type="email"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your email"
                />
                {errorsRequest.email && (
                  <p className="text-red-400 text-sm mt-1">
                    {errorsRequest.email.message}
                  </p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.32, duration: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Smartphone size={16} className="inline mr-2" />
                  Mobile Number
                </label>
                <input
                  {...registerRequest("mobile", {
                    required: "Mobile number is required",
                  })}
                  type="tel"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your mobile number"
                />
                {errorsRequest.mobile && (
                  <p className="text-red-400 text-sm mt-1">
                    {errorsRequest.mobile.message}
                  </p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.34, duration: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Hash size={16} className="inline mr-2" />
                  GST Number
                </label>
                <input
                  {...registerRequest("gstNo", {
                    required: "GST number is required",
                  })}
                  type="text"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter GST number"
                />
                {errorsRequest.gstNo && (
                  <p className="text-red-400 text-sm mt-1">
                    {errorsRequest.gstNo.message}
                  </p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.36, duration: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Key size={16} className="inline mr-2" />
                  Tally Serial Number
                </label>
                <input
                  {...registerRequest("tally_serialno", {
                    required: "Tally serial number is required",
                  })}
                  type="text"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter Tally serial number"
                />
                {errorsRequest.tally_serialno && (
                  <p className="text-red-400 text-sm mt-1">
                    {errorsRequest.tally_serialno.message}
                  </p>
                )}
              </motion.div>

              {message && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-green-400 text-sm text-center"
                >
                  {message}
                </motion.p>
              )}

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading}
                className="w-full banking-gradient py-3 px-4 rounded-lg text-white font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Send Reset Link"
                )}
              </motion.button>
            </form>
          ) : (
            <form
              onSubmit={handleSubmitConfirm(onSubmitConfirm)}
              className="space-y-6"
            >
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  New Password
                </label>
                <input
                  {...registerConfirm("newPassword", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  type="password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter new password"
                />
                {errorsConfirm.newPassword && (
                  <p className="text-red-400 text-sm mt-1">
                    {errorsConfirm.newPassword.message}
                  </p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password
                </label>
                <input
                  {...registerConfirm("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === watchPassword || "Passwords do not match",
                  })}
                  type="password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Confirm new password"
                />
                {errorsConfirm.confirmPassword && (
                  <p className="text-red-400 text-sm mt-1">
                    {errorsConfirm.confirmPassword.message}
                  </p>
                )}
              </motion.div>

              {message && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-green-400 text-sm text-center"
                >
                  {message}
                </motion.p>
              )}

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading}
                className="w-full banking-gradient py-3 px-4 rounded-lg text-white font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Reset Password"
                )}
              </motion.button>
            </form>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="text-center mt-6"
          >
            <Link
              to="/login"
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors flex items-center justify-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Login
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
